// Nederlandse kalenderdagen uit (UTC-)tijdstempels — tijdzone-ronde 2, 23 okt 2026.
//
// Draaidagen staan in Outlook/Airtable als tijdstempel: een hele dag op 24 nov is
// 2026-11-23T23:00Z tot 2026-11-24T23:00Z (einde exclusief, 25 nov 00:00 NL). De
// PG-spiegel levert die naïef in UTC, zonder 'Z'; `new Date()` las ze dan als lokale
// tijd en de draaidag stond een dag te vroeg en besloeg twee dagen. Deze helpers geven
// de dag zoals Outlook hem toont, in Europe/Amsterdam, met een inclusief einde.
// Zelfde regels als `core/tijd.py` (dag_nl / einddag_nl / nl_tijdstip_iso).
import { parseUtcDate } from './utcDate';

const TIJDZONE = 'Europe/Amsterdam';
const PUUR = /^\d{4}-\d{2}-\d{2}$/;
const pad = (n) => String(n).padStart(2, '0');

const onderdelen = (dt) => {
  const p = {};
  new Intl.DateTimeFormat('en-GB', {
    timeZone: TIJDZONE, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(dt).forEach(({ type, value }) => { p[type] = Number(value); });
  return p;
};

const alsDatum = (waarde) => {
  if (waarde instanceof Date) return Number.isNaN(waarde.getTime()) ? null : waarde;
  const dt = parseUtcDate(String(waarde).trim());   // naïef = UTC, zoals overal in de database
  return dt && !Number.isNaN(dt.getTime()) ? dt : null;
};

/** 'YYYY-MM-DD' een dag later (of eerder), zonder tijdzone. */
export function dagErna(iso, aantal = 1) {
  const [j, m, d] = String(iso).split('-').map(Number);
  const dt = new Date(Date.UTC(j, m - 1, d + aantal));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}

/** De NL-kalenderdag (YYYY-MM-DD) van een datum of tijdstempel; een pure datum blijft staan. */
export function nlDag(waarde) {
  if (waarde === null || waarde === undefined || waarde === '') return null;
  if (typeof waarde === 'string' && PUUR.test(waarde.trim())) return waarde.trim();
  const dt = alsDatum(waarde);
  if (!dt) return null;
  const p = onderdelen(dt);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

/**
 * De laatste NL-kalenderdag, INCLUSIEF. Een tijdstempel-einde op 00:00 NL na het begin
 * is exclusief (Outlook): dan is de dag ervóór de laatste. Een pure datum blijft staan.
 */
export function nlEinddag(waarde, begin = null) {
  if (waarde === null || waarde === undefined || waarde === '') return null;
  if (typeof waarde === 'string' && PUUR.test(waarde.trim())) return waarde.trim();
  const dt = alsDatum(waarde);
  if (!dt) return null;
  const p = onderdelen(dt);
  const dag = `${p.year}-${pad(p.month)}-${pad(p.day)}`;
  const start = nlDag(begin);
  if (p.hour === 0 && p.minute === 0 && p.second === 0 && (!start || dag > start)) return dagErna(dag, -1);
  return dag;
}

const puur = (v) => {
  const s = String(Array.isArray(v) ? v[0] || '' : v || '').slice(0, 10);
  return PUUR.test(s) ? s : null;
};

const utcDag = (waarde) => {
  const dt = waarde ? alsDatum(waarde) : null;
  return dt ? dt.toISOString().slice(0, 10) : null;
};

// Pure datum, tenzij het de UTC-terugval van de sync is (lege Airtable-formule → UTC-dag
// van de Outlook-tijd). Een echte formulewaarde is de NL-dag; zie core/tijd.startdag_nl.
const puurOfNl = (pure, outlook, nl) => {
  const p = puur(pure);
  if (p && nl && p !== nl && utcDag(outlook) === p) return nl;
  return p || nl;
};

/**
 * Begin- en einddag (inclusief, NL) van een draaidag-record. Pure StartDatum/EindDatum
 * eerst; anders de Outlook-tijden omgerekend.
 */
export function draaidagPeriode(fields = {}) {
  const f = fields || {};
  const start = puurOfNl(f.StartDatum, f.StartdatumOutlook, nlDag(f.StartdatumOutlook));
  let eind = puurOfNl(f.EindDatum, f.EinddatumOutlook, nlEinddag(f.EinddatumOutlook, start)) || start;
  if (start && eind && eind < start) eind = start;
  return { start: start || null, eind: eind || null };
}

/**
 * Aantal kalenderdagen van vandaag (NL) tot de dag van `waarde`: 0 = vandaag, 1 = morgen.
 * `Math.ceil((new Date('2026-10-25') - new Date()) / dag)` gaf om 00:30 NL 'morgen' voor
 * vandaag, omdat een pure datum als 00:00 UTC (= 02:00 NL) wordt gelezen.
 */
export function dagenTot(waarde, nu = new Date()) {
  const doel = nlDag(waarde);
  const vandaag = nlDag(nu);
  if (!doel || !vandaag) return null;
  const ms = (iso) => { const [j, m, d] = iso.split('-').map(Number); return Date.UTC(j, m - 1, d); };
  return Math.round((ms(doel) - ms(vandaag)) / 86400000);
}

/**
 * ISO-tijdstip mét offset voor een NL-dag + kloktijd ('2026-10-25', '23:59:59' →
 * '2026-10-25T23:59:59+01:00'). Graph `calendarView` leest een tijd zonder offset als UTC.
 */
export function nlTijdstipIso(dag, tijd = '00:00:00') {
  const [j, m, d] = String(dag).split('-').map(Number);
  const [u, mi, s = 0] = String(tijd).split(':').map(Number);
  const naief = Date.UTC(j, m - 1, d, u, mi, s);
  const offset = (ms) => {
    const p = onderdelen(new Date(ms));
    return Math.round((Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - ms) / 60000);
  };
  let minuten = offset(naief - 60 * 60000);
  minuten = offset(naief - minuten * 60000);
  const teken = minuten >= 0 ? '+' : '-';
  const a = Math.abs(minuten);
  return `${dag}T${pad(u)}:${pad(mi)}:${pad(s)}${teken}${pad(Math.floor(a / 60))}:${pad(a % 60)}`;
}
